import React from 'react';
import {
  AgnesAutocomplete,
  AgnesButton,
  AgnesLabel,
  AgnesModal,
  AgnesSelect,
  AgnesSelectContent,
  AgnesSelectItem,
  AgnesSelectTrigger,
  AgnesSelectValue,
} from '@agnes/ui';
import { CENTRAL_REPROCESSAMENTO_TAMANHO_ENUM } from '@/constants/central-reprocessamento/ConstantCentralReprocessamentoTamanho';
import { CENTRAL_REPROCESSAMENTO_ERRO_ENUM } from '@/constants/central-reprocessamento/ConstantCentralReprocessamentoErro';
import { ModelCentralReprocessamentoFormulario } from '@/model/central-reprocessamento/ModelCentralReprocessamentoFormulario';
import { useCentralReprocessamentoStore } from '@/stores/useCentralReprocessamentoStore';
import { type RootCnpjItem, useCompaniesStore } from '@/stores/useCompaniesStore';
import { confirm } from '@/stores/useConfirm';
import { toOptions } from '@/utils/options';
import { employerListMessage } from '@/utils/employerListMessage';
import { EmployerListRetryButton } from '@/components/EmployerListRetryButton';
import {
  CENTRAL_REPROCESSAMENTO_TIPO,
  CentralReprocessamentoTipo,
} from '@/constants/central-reprocessamento/CentralReprocessamentoTipo';
import {
  CENTRAL_REPROCESSAMENTO_TAMANHO,
  CentralReprocessamentoTamanho,
} from '@/constants/central-reprocessamento/CentralReprocessamentoTamanho';

export interface FormularioDialogProps {
  open: boolean;
  id?: number;
  onClose: () => void;
  onConfirm: () => void;
}

const optionsCentralReprocessamentoTamanhos = toOptions(
  Object.keys(CENTRAL_REPROCESSAMENTO_TAMANHO) as CentralReprocessamentoTamanho[],
  { labelTransformerFn: (v: CentralReprocessamentoTamanho) => CENTRAL_REPROCESSAMENTO_TAMANHO[v] },
);
const optionsCentralReprocessamentoTipos = toOptions(
  Object.keys(CENTRAL_REPROCESSAMENTO_TIPO) as CentralReprocessamentoTipo[],
  { labelTransformerFn: (v: CentralReprocessamentoTipo) => CENTRAL_REPROCESSAMENTO_TIPO[v] },
);

function tamanhoRecomendado(reprocessamento: {
  erro?: { codigo?: string } | null;
  tamanho?: string;
}): string {
  const atual = reprocessamento.tamanho ?? CENTRAL_REPROCESSAMENTO_TAMANHO_ENUM.PEQUENO.getValue();
  const memoriaInsuficiente =
    reprocessamento.erro?.codigo ===
    CENTRAL_REPROCESSAMENTO_ERRO_ENUM.MEMORIA_INSUFICIENTE.getValue();
  if (!memoriaInsuficiente) return atual;
  if (atual === CENTRAL_REPROCESSAMENTO_TAMANHO_ENUM.PEQUENO.getValue())
    return CENTRAL_REPROCESSAMENTO_TAMANHO_ENUM.MEDIO.getValue();
  return CENTRAL_REPROCESSAMENTO_TAMANHO_ENUM.GRANDE.getValue();
}

export function CentralReprocessamentoFormularioDialog({
  open,
  id,
  onClose,
  onConfirm,
}: Readonly<FormularioDialogProps>) {
  const isResolicitar = Boolean(id);
  const estaCriando = useCentralReprocessamentoStore((s) => s.estaCriandoReprocessamento);
  const rootCnpjs = useCompaniesStore((s) => s.rootCnpjs);
  const carregandoEmpresas = useCompaniesStore((s) => s.loading);
  const empresasCarregadas = useCompaniesStore((s) => s.loaded);
  const erroEmpresas = useCompaniesStore((s) => s.erro);

  const [formulario, setFormulario] = React.useState(
    () => new ModelCentralReprocessamentoFormulario(),
  );
  const [empresasSelecionadas, setEmpresasSelecionadas] = React.useState<RootCnpjItem[]>([]);

  React.useEffect(() => {
    if (!open) return;
    if (!isResolicitar) {
      useCompaniesStore.getState().fetchRootCnpjs();
      return;
    }
    (async () => {
      const r = await useCentralReprocessamentoStore.getState().buscar(id as number, true);
      if (!r) return;
      const f = new ModelCentralReprocessamentoFormulario();
      f.nrinsc = Array.isArray(r.nrinsc) ? r.nrinsc : [r.nrinsc];
      f.tipo = r.tipo;
      f.tamanho = tamanhoRecomendado(r);
      setFormulario(f);
    })();
  }, [open, id, isResolicitar]);

  const isFormValid = isResolicitar
    ? Boolean(formulario.tamanho)
    : empresasSelecionadas.length > 0 && Boolean(formulario.tipo) && Boolean(formulario.tamanho);

  function reiniciar() {
    setFormulario(new ModelCentralReprocessamentoFormulario());
    setEmpresasSelecionadas([]);
  }

  function toggleEmpresa(opt: RootCnpjItem) {
    setEmpresasSelecionadas((prev) =>
      prev.some((e) => e.value === opt.value)
        ? prev.filter((e) => e.value !== opt.value)
        : [...prev, opt],
    );
  }

  async function onClickConfirmar() {
    if (!isFormValid) return;
    onClose();

    if (isResolicitar) {
      const ok = await confirm({
        message: 'Você tem certeza de que deseja resolicitar o reprocessamento?',
        okLabel: 'Sim, confirmar',
        cancelLabel: 'Não, cancelar',
      });
      if (ok) {
        await useCentralReprocessamentoStore.getState().resolicitar(id as number, formulario);
        onConfirm();
      }
      reiniciar();
      return;
    }

    const payload = new ModelCentralReprocessamentoFormulario();
    payload.tipo = formulario.tipo;
    payload.tamanho = formulario.tamanho;
    payload.nrinsc = empresasSelecionadas.map((e) => e.value);

    const ok = await confirm({
      message: 'Você tem certeza de que deseja criar o reprocessamento?',
      okLabel: 'Sim, confirmar',
      cancelLabel: 'Não, cancelar',
    });
    if (ok) {
      await useCentralReprocessamentoStore.getState().criar(payload);
      onConfirm();
    }
    reiniciar();
  }

  return (
    <AgnesModal
      open={open}
      maxWidth="sm"
      title={isResolicitar ? 'Resolicitar reprocessamento' : 'Novo reprocessamento'}
      onClose={() => {
        reiniciar();
        onClose();
      }}
      footer={
        <div className="flex justify-end gap-3">
          <AgnesButton
            variant="text"
            onClick={() => {
              reiniciar();
              onClose();
            }}
          >
            Cancelar
          </AgnesButton>
          <AgnesButton
            variant="primary"
            loading={estaCriando}
            disabled={!isFormValid}
            onClick={onClickConfirmar}
          >
            Confirmar
          </AgnesButton>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <AgnesAutocomplete<RootCnpjItem>
          label="Empresas"
          options={rootCnpjs}
          value={empresasSelecionadas}
          onOptionToggle={toggleEmpresa}
          getOptionLabel={(o) => `${o.title} - ${o.subtitle}`}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          placeholder="Selecione as empresas"
          noOptionsText={employerListMessage({
            loading: carregandoEmpresas,
            loaded: empresasCarregadas,
            error: erroEmpresas,
          })}
          disabled={isResolicitar}
        />
        <EmployerListRetryButton
          error={erroEmpresas}
          onRetry={() => void useCompaniesStore.getState().fetchRootCnpjs()}
        />

        <div>
          <AgnesLabel required>Tipo</AgnesLabel>
          <AgnesSelect
            value={formulario.tipo ?? ''}
            onValueChange={(v: string) =>
              setFormulario((f) =>
                Object.assign(new ModelCentralReprocessamentoFormulario(), f, { tipo: v }),
              )
            }
            disabled={isResolicitar}
          >
            <AgnesSelectTrigger>
              <AgnesSelectValue placeholder="Selecione o tipo" />
            </AgnesSelectTrigger>
            <AgnesSelectContent>
              {optionsCentralReprocessamentoTipos.map((item) => (
                <AgnesSelectItem key={item.value} value={item.value}>
                  {item.label}
                </AgnesSelectItem>
              ))}
            </AgnesSelectContent>
          </AgnesSelect>
        </div>

        <div>
          <AgnesLabel required>Tamanho</AgnesLabel>
          <AgnesSelect
            value={formulario.tamanho ?? ''}
            onValueChange={(v: string) =>
              setFormulario((f) =>
                Object.assign(new ModelCentralReprocessamentoFormulario(), f, { tamanho: v }),
              )
            }
          >
            <AgnesSelectTrigger>
              <AgnesSelectValue placeholder="Selecione o tamanho" />
            </AgnesSelectTrigger>
            <AgnesSelectContent>
              {optionsCentralReprocessamentoTamanhos.map((item) => (
                <AgnesSelectItem key={item.value} value={item.value}>
                  {item.label}
                </AgnesSelectItem>
              ))}
            </AgnesSelectContent>
          </AgnesSelect>
        </div>
      </div>
    </AgnesModal>
  );
}
